import { ICreateBookingRequest, IBooking } from '../interfaces/heavyDutyMachinery/IBooking';
import { apiService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

export const createBooking = async (request: ICreateBookingRequest): Promise<IBooking> => {
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKINGS,
    method: HTTP_METHODS.POST,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to create booking');
};

export const getBookingById = async (id: string): Promise<IBooking> => {
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_BY_ID(id),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load booking');
};

export const getMyBookings = async (): Promise<IBooking[]> => {
  const response = await apiService<IBooking[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_MY_BOOKINGS,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load your bookings');
};

export const getIncomingBookings = async (): Promise<IBooking[]> => {
  const response = await apiService<IBooking[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_INCOMING_BOOKINGS,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load incoming bookings');
};

export const cancelBooking = async (id: string): Promise<IBooking> => {
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to cancel booking');
};

/** Host-only counterpart to cancelBooking -- always refunds in full and skips the guest's own CancellationPolicy window, since this is the host's decision, not the guest's. */
export const hostCancelBooking = async (id: string, reason: string): Promise<IBooking> => {
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_HOST_CANCEL(id),
    method: HTTP_METHODS.POST,
    body: { reason },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to cancel booking');
};

/**
 * Hands part of a booking's money back without ending it -- the host's own
 * action, for everything short of a cancellation: a machine that turned up
 * late, a day that could not be worked, damage settled at less than the
 * whole.
 *
 * amount is in the booking's hostCurrencyCode (the currency totalPrice is
 * shown in), and must not exceed booking.refundableRemaining -- which the
 * server computes and re-checks, so the form's own maximum and the rule that
 * is actually enforced can never drift apart.
 *
 * reason is not internal: the guest reads it in their notification and it is
 * printed on the rectificativa as the grounds for the correction.
 */
export const refundBookingPartially = async (id: string, amount: number, reason: string): Promise<IBooking> => {
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_REFUND(id),
    method: HTTP_METHODS.POST,
    body: { amount, reason },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to refund booking');
};

// ---- Rental lifecycle milestones ------------------------------------------
// All three are idempotent server-side (a second call is a no-op returning
// the same booking, see each action's own doc comment) -- these are buttons
// pressed one-handed from a cab, so a double tap must never become an error
// the driver has to make sense of.

export const markTransportStarted = async (id: string): Promise<IBooking> => {
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_TRANSPORT_STARTED(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to start transport');
};

export const declareOffRent = async (id: string): Promise<IBooking> => {
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_OFF_RENT(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to declare end of work');
};

export const markCollectionStarted = async (id: string): Promise<IBooking> => {
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_COLLECTION_STARTED(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to start collection');
};

export const markBookingNoShow = async (id: string, party: 'Host' | 'Guest'): Promise<IBooking> => {
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_NO_SHOW(id),
    method: HTTP_METHODS.POST,
    params: { party },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to mark booking no-show');
};

/** Re-opens checkout for a Pending booking the guest never finished paying -- the response's clientSecret is the same shape createBooking's own response carries. */
export const resumeBookingPayment = async (id: string): Promise<IBooking> => {
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_RESUME_PAYMENT(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to resume payment');
};

// ---- Reserva diferida -- only meaningful for a booking whose status is
// AwaitingQuote/AwaitingPayment (Machinery.deferredBookingEnabled). ----

/** Either party (whoever's turn it is) locks in the current quoteCurrentAmount and pays -- mints the clientSecret, same shape createBooking's own response carries. */
export const acceptQuoteBooking = async (id: string): Promise<IBooking> => {
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_ACCEPT_QUOTE(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to accept quote');
};

/** Host-only. Appends a new quoteHistory revision with a mandatory reason -- doesn't change status, the guest can accept it or the host can revise again. */
/** One line the host is proposing -- only ever the ones they're allowed to set. */
export interface IProposedQuoteLine {
  title: string;
  description?: string;
  amount: number;
  origin: string;
  /**
   * The trade-quote shape, when the line has one: how many, at what each, in
   * what unit. Sent together or not at all -- when both arrive the server
   * DERIVES amount from them rather than trusting the figure we computed, so
   * a client that rounds differently can never produce a quote whose lines
   * do not add up to what the customer is asked to pay.
   */
  quantity?: number;
  unitPrice?: number;
  unit?: string;
  /** ProviderRate | Estimated | Manual -- carried through so the saved revision keeps saying which prices were guessed at. */
  priceSource?: string;
  /** The rate-card entry this line was priced from, when it was priced from one. */
  catalogKey?: string;
}

/**
 * Host-only. Sends the itemised quote: only the editable lines travel, and
 * the server re-attaches whatever is owed to somebody else and computes the
 * total itself (see the backend's ProposeQuoteAmountRequest). `amount` is
 * still sent as the legacy single-figure fallback for a server that predates
 * the itemised editor.
 */
export const proposeNewQuoteAmount = async (id: string, lines: IProposedQuoteLine[], reason: string): Promise<IBooking> => {
  const amount = lines.reduce((sum, line) => sum + line.amount, 0);
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_PROPOSE_QUOTE_AMOUNT(id),
    method: HTTP_METHODS.POST,
    body: { amount, lines, reason },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to propose new amount');
};

/** Host-only. Rejects the WHOLE quote request (not one specific revision) -- same required-reason shape as hostCancelBooking. */
export const rejectQuoteBooking = async (id: string, reason: string): Promise<IBooking> => {
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_REJECT_QUOTE(id),
    method: HTTP_METHODS.POST,
    body: { reason },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to reject quote');
};

/** Guest self-service -- cancels the whole request while AwaitingQuote/AwaitingPayment, no CancellationPolicy window (that governs backing out of a CONFIRMED commitment, not an unpriced/unpaid one). */
export const cancelQuoteBooking = async (id: string): Promise<IBooking> => {
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_QUOTE(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to cancel quote');
};

// ---- Reservas de servicio que se cobran al terminar (Booking.CaptureMode
// OnHandover sobre una reserva sin maquinaria). ----

/**
 * The customer asks for their own card-saving secret.
 *
 * It is a POST of its own rather than something handed out when the booking is
 * made, because of who makes it: on a service booking the PROFESSIONAL confirms
 * the visit, and a secret returned to them would be a secret for somebody
 * else's card. So the customer comes and asks for theirs -- same shape and same
 * reason as resumeBookingPayment above.
 *
 * Comes back in setupClientSecret, NOT clientSecret: it is confirmed with
 * Stripe.js's confirmSetup, and confirming it as a payment would charge
 * somebody who was told nothing would be taken. See SaveCardStep.
 */
export const startBookingCardSetup = async (id: string): Promise<IBooking> => {
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_CARD_SETUP(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to start card setup');
};

/**
 * The professional declares the work done -- the service side's albarán, and
 * the single most consequential button either of them presses.
 *
 * It captures the held money (BookingCaptureHelper.CaptureOnServiceCompletedAsync)
 * BEFORE it stamps the milestone, so a refused card leaves the booking saying
 * the job is still open rather than finished-and-unpaid. And it starts the
 * customer's own 24 hours before the funds are released.
 */
export const markServiceCompleted = async (id: string): Promise<IBooking> => {
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_SERVICE_COMPLETED(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to mark the service as completed');
};
